import React, { useEffect, useState } from 'react'
import { Modal, Checkbox, Table, Radio } from 'semantic-ui-react'
import { TemplateUserService } from 'utility/services/template-user'
import { LoaderBar } from 'components'
import styled from 'styled-components'
import { X } from 'phosphor-react'
import { SubmitCancelButtons } from 'elements/Button'

const ScrollableContent = styled(Modal.Content)`
    max-height: 60vh;
    overflow-y: auto;

    .ui.table {
        border: none;
        margin-top: 0;
    }
`

const ReadOnlyCell = styled(Table.Cell)`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    text-align: right;
    width: 120px;
    padding-right: 14px !important;
`

export const TemplateAssignModal = ({ open, onClose, userId }) => {
    const [templates, setTemplates] = useState([])
    const [loading, setLoading] = useState(false)
    const [selectAll, setSelectAll] = useState(false)

    // Reset state when modal closes
    useEffect(() => {
        if (!open) {
            setTemplates([])
            setSelectAll(false)
        }
    }, [open])

    useEffect(() => {
        if (open && userId) {
            fetchTemplates()
        }
    }, [open, userId])

    const fetchTemplates = async () => {
        try {
            setLoading(true)
            const response = await TemplateUserService.getByUserId(userId)
            // Ensure we're not duplicating entries by using TemplateId as unique identifier
            const uniqueTemplates = response.reduce((acc, template) => {
                if (!acc.find(t => t.TemplateId === template.TemplateId)) {
                    // Prioritize IsReadOnly from API response, fallback to parsing if needed
                    let isReadOnly = false
                    let actualTemplateId = template.TemplateId

                    // First check if IsReadOnly is directly available in the response
                    if (template.IsReadOnly !== undefined) {
                        isReadOnly = template.IsReadOnly === true
                    } else if (template.TemplateId && typeof template.TemplateId === 'string' && template.TemplateId.includes('-')) {
                        // Fallback: Parse IsReadOnly from TemplateId format "Id-0" or "Id-1"
                        // Mapping: 0 = IsReadOnly false, 1 = IsReadOnly true
                        const parts = template.TemplateId.split('-')
                        if (parts.length === 2) {
                            const [id, readonlyFlag] = parts
                            isReadOnly = readonlyFlag === '1'
                            actualTemplateId = id // Extract actual ID
                        }
                    }

                    acc.push({
                        ...template,
                        TemplateId: actualTemplateId,
                        IsReadOnly: isReadOnly,
                    })
                }
                return acc
            }, [])
            setTemplates(uniqueTemplates)
            // Check if all templates are selected
            const allSelected = uniqueTemplates.every(template => template.IsSelected)
            setSelectAll(allSelected)
        } catch (error) {
            console.error('Error fetching templates:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleSelectAll = () => {
        const newSelectAll = !selectAll
        setSelectAll(newSelectAll)
        setTemplates(
            templates.map(template => ({
                ...template,
                IsSelected: newSelectAll,
            })),
        )
    }

    const handleTemplateSelect = templateId => {
        const updatedTemplates = templates.map(template =>
            template.TemplateId === templateId
                ? {
                      ...template,
                      IsSelected: !template.IsSelected,
                      IsReadOnly: !template.IsSelected ? false : template.IsReadOnly, // Reset IsReadOnly when deselected
                  }
                : template,
        )
        setTemplates(updatedTemplates)
        setSelectAll(updatedTemplates.every(template => template.IsSelected))
    }

    const handleReadOnlyToggle = templateId => {
        setTemplates(
            templates.map(template =>
                template.TemplateId === templateId
                    ? { ...template, IsReadOnly: !template.IsReadOnly }
                    : template,
            ),
        )
    }

    const handleSave = async () => {
        try {
            setLoading(true)
            // Format selected template IDs as "Id-0" (not readonly) or "Id-1" (readonly)
            // Mapping: IsReadOnly false → send 0, IsReadOnly true → send 1
            // Example: "37753-0" means IsReadOnly=false, "37754-1" means IsReadOnly=true
            const selectedTemplateIds = templates
                .filter(template => template.IsSelected)
                .map(template => {
                    const readonlyFlag = template.IsReadOnly === true ? '1' : '0'
                    return `${template.TemplateId}-${readonlyFlag}`
                })
                .join(';')

            const unselectedTemplateIds = templates
                .filter(template => !template.IsSelected)
                .map(template => template.TemplateId)
                .join(';')

            const payload = {
                UserId: userId,
                TemplateIds: selectedTemplateIds,
                UnselectedTemplateIds: unselectedTemplateIds,
            }

            // If there are any templates in the list, use update (edit mode)
            // Otherwise, use create (new assignment or all unchecked)
            if (templates.some(t => t.IsSelected || !t.IsSelected)) {
                await TemplateUserService.update(payload)
            } else {
                await TemplateUserService.create(payload)
            }
            onClose(true)
        } catch (error) {
            console.error('Error saving template assignments:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleClose = () => {
        setTemplates([])
        setSelectAll(false)
        onClose(false)
    }

    return (
        <Modal open={open} onClose={handleClose} size="small">
            <Modal.Header>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{templates.some(t => t.IsSelected) ? 'Edit Assign Template' : 'Assign Template'}</span>
                    <X className="pointer" size={32} onClick={handleClose} />
                </div>
            </Modal.Header>
            <ScrollableContent>
                {loading && <LoaderBar content="Loading" />}
                <Table>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>
                                <Checkbox
                                    checked={selectAll}
                                    onChange={handleSelectAll}
                                    label="Select All"
                                />
                            </Table.HeaderCell>
                            <Table.HeaderCell>Template Name</Table.HeaderCell>
                            <Table.HeaderCell textAlign="right" style={{ width: '120px', paddingRight: '14px' }}>IsReadOnly</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {templates && templates.length > 0 ? (
                            templates.map(template => (
                                <Table.Row key={template.TemplateId}>
                                    <Table.Cell collapsing>
                                        <Checkbox
                                            checked={template.IsSelected}
                                            onChange={() =>
                                                handleTemplateSelect(
                                                    template.TemplateId,
                                                )
                                            }
                                        />
                                    </Table.Cell>
                                    <Table.Cell>
                                        {template.TemplateName || 'Unnamed Template'}
                                    </Table.Cell>
                                    <ReadOnlyCell>
                                        <Radio
                                            toggle
                                            checked={template.IsReadOnly === true}
                                            onChange={() =>
                                                handleReadOnlyToggle(
                                                    template.TemplateId,
                                                )
                                            }
                                            disabled={!template.IsSelected}
                                        />
                                    </ReadOnlyCell>
                                </Table.Row>
                            ))
                        ) : (
                            <Table.Row>
                                <Table.Cell colSpan="3" textAlign="center">
                                    No templates available
                                </Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            </ScrollableContent>
            <Modal.Actions>
                <SubmitCancelButtons
                    loading={loading}
                    onSubmit={handleSave}
                    onCancel={handleClose}
                    hideCancel={false}
                    cancelText="Cancel"
                />
            </Modal.Actions>
        </Modal>
    )
}
